import { ChangeEvent, useId } from 'react';
import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { Pagination, Select } from '@morressier/alembic';

/** Page sizes offered by the records-per-page selector. */
export const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100];

export const DEFAULT_PAGE_SIZE = 10;

export interface ITablePaginationProps {
  /** Current page, 1-based. */
  currentPage: number;
  /** Total number of records across every page. */
  totalItems: number;
  /** Number of records requested per page. */
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Selectable page sizes. Defaults to {@link PAGE_SIZE_OPTIONS}. */
  pageSizeOptions?: number[];
  /** Label rendered beside the page size select. */
  pageSizeLabel?: string;
  /** Hides the records-per-page selector when only page navigation is wanted. */
  showPageSizeSelector?: boolean;
  /** Accessible label for the page navigation controls. */
  ariaLabel?: string;
}

/**
 * Footer for paginated tables: a records-per-page selector alongside the page
 * navigation. Fully controlled — the caller owns `currentPage` and `pageSize`
 * because both feed into the query offset.
 *
 * Changing the page size also resets to page 1, since the current offset may
 * no longer exist under the new page size.
 */
const TablePagination = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  pageSizeLabel = 'Rows per page',
  showPageSizeSelector = true,
  ariaLabel = 'Pagination'
}: ITablePaginationProps) => {
  const pageSizeSelectId = useId();
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextPageSize = Number(event.currentTarget.value);
    if (!nextPageSize || nextPageSize === pageSize) return;

    onPageSizeChange(nextPageSize);
    onPageChange(1);
  };

  if (totalItems <= 0) return null;

  return (
    // Three balanced columns keep the page navigation centred on the page
    // whether or not the records-per-page selector is rendered.
    <Grid
      templateColumns={{ base: '1fr', md: '1fr auto 1fr' }}
      alignItems="center"
      gap={3}
      marginY={3}
      width="100%"
    >
      <Flex
        alignItems="center"
        gap={2}
        justifySelf={{ base: 'center', md: 'start' }}
      >
        {showPageSizeSelector && (
          <>
            <Text
              as="label"
              htmlFor={pageSizeSelectId}
              textStyle="textBody"
              whiteSpace="nowrap"
            >
              {pageSizeLabel}
            </Text>
            <Select
              id={pageSizeSelectId}
              aria-label={pageSizeLabel}
              width="auto"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </>
        )}
      </Flex>
      <Flex justifyContent="center">
        {totalPages > 1 && (
          <Pagination
            key={`pagination-${totalPages}`}
            ariaLabel={ariaLabel}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        )}
      </Flex>
      <Box display={{ base: 'none', md: 'block' }} />
    </Grid>
  );
};

export default TablePagination;
